import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

// One toast at a time, bottom centre. Status and grade changes offer Undo for 6 seconds (handoff: Undo).

interface ToastSpec {
  text: string;
  undo?: () => Promise<unknown>;
}

const Ctx = createContext<(t: ToastSpec) => void>(() => {});
export const useToast = () => useContext(Ctx);

const DURATION_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<(ToastSpec & { key: number }) | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const show = useCallback((t: ToastSpec) => {
    clearTimeout(timer.current);
    setBusy(false);
    setToast({ ...t, key: Date.now() });
  }, []);

  useEffect(() => {
    if (!toast) return;
    timer.current = setTimeout(() => setToast(null), DURATION_MS);
    return () => clearTimeout(timer.current);
  }, [toast]);

  const undo = async () => {
    if (!toast?.undo || busy) return;
    clearTimeout(timer.current);
    setBusy(true);
    try {
      await toast.undo();
      setToast({ text: 'Undone', key: Date.now() });
    } catch (e) {
      setToast({ text: e instanceof Error ? e.message : 'Couldn’t undo', key: Date.now() });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Ctx.Provider value={show}>
      {children}
      {toast && (
        <div className="toast" role="status" key={toast.key}>
          <span>{toast.text}</span>
          {toast.undo && (
            <button onClick={undo} disabled={busy}>
              {busy ? 'Undoing…' : 'Undo'}
            </button>
          )}
        </div>
      )}
    </Ctx.Provider>
  );
}
